
import { Component, Prop, Vue } from 'vue-property-decorator';
import { DateTime } from 'luxon';

import SelectField from '../../../../framework/fields/SelectField.vue';
import { sharedState } from '../../../../framework/state';
import {
    deleteResource,
    getResource,
    getResourcePagingNew,
    getResourcesNew,
} from '../../../../framework/client/resource';

import { Customer } from '../../../../../common/application/model/Customer';
import { VehicleContract } from '../../../../../common/application/model/VehicleContract';
import { ApplicationUserRole } from '../../../../../common/application/enumeration/ApplicationUserRole';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';

import { VehicleUsage } from '../../../../../common/application/model/VehicleUsage';
import { getMaponVehicleUsage } from '../../../client/mapon_client';
import { getGSGVehicleUsage } from '../../../client/gsg_client';
import { getAbaxVehicleUsage } from '../../../client/abax_client';
import { formatToFinnishDate } from '../../../service/helper_service';
import { errorToast, successToast } from '../../../service/toast_service';
import ParameterService from '../../../service/parameter_service';

@Component({
    components: {
        SelectField,
    },
})
export default class VehicleUsages extends Vue {
    readonly resourceType = ApplicationResource.VEHICLE_USAGE;
    parameterService = new ParameterService(this.resourceType);

    @Prop(String) readonly vehicleContractId!: string;

    shared = sharedState;
    local = {
        rows: [] as VehicleUsage[],
        total: 0,
        loading: false,
        selected: undefined as VehicleUsage | undefined,
        vehicleContract: {} as VehicleContract,
        parameters: this.parameterService.get(),
    };

    async mounted() {
        await this.loadAsyncData();
    }

    confirmDelete(id?: string) {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                const deletableId = id ? id : this.local.selected ? this.local.selected.id : undefined;

                if (deletableId) {
                    try {
                        await deleteResource(this.resourceType, deletableId);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    async updateLastMonthData() {
        if (this.local.vehicleContract.maponUnitId) {
            await getMaponVehicleUsage(this.local.vehicleContract.customerId, this.local.vehicleContract.maponUnitId);
        }

        if (this.local.vehicleContract.gsgUnitId) {
            await getGSGVehicleUsage(this.local.vehicleContract.customerId, this.local.vehicleContract.gsgUnitId);
        }

        if (this.local.vehicleContract.abaxVehicleId) {
            await getAbaxVehicleUsage(this.local.vehicleContract.customerId, this.local.vehicleContract.abaxVehicleId);
        }

        await this.loadAsyncData();
    }

    async onSortChange(field: string, order: 'desc' | 'asc') {
        this.local.parameters.sortField = field;
        this.local.parameters.sortOrder = order;
        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    get customerIds(): string[] {
        return this.local.parameters.customerId || [];
    }

    onRowClick(row: Customer): void {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    formatDate(date: string): string {
        return formatToFinnishDate(date);
    }

    vehicleUsageMonth(date: string): string {
        return DateTime.fromJSDate(new Date(date)).month.toString();
    }

    private async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();

        if (this.vehicleContractId) {
            backendParameters.set('vehicleContractId', this.vehicleContractId);
            this.local.vehicleContract = (await getResource<VehicleContract>(
                ApplicationResource.VEHICLE_CONTRACT,
                this.vehicleContractId,
            ))!!;
        }

        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters)).rowCount;

        this.local.rows = [];

        const rows: VehicleUsage[] = await getResourcesNew(
            this.resourceType,
            this.local.parameters.page,
            backendParameters,
        );

        rows.forEach((row) => {
            this.local.rows.push(row);
        });

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        this.local.loading = false;
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
